/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect, replace } from 'react-router'
import type { IamFeature } from '@/features/iam'
import type { TeamsViewModel } from '@/features/iam/teams/TeamsViewModel'
import type { TeamViewModel } from '@/features/iam/teams/TeamViewModel'
import { isAbortError } from '@/utils/abort-util'
import { links } from '@/utils/link'
import type { ProfileViewModel } from '@/features/iam/users/ProfileViewModel'

/**
 * Data returned by {@link createTeamsViewLoader}. `null` when no workspace is
 * selected yet, so the authentication screen is showing.
 */
export type TeamsViewLoaderData = { vm: TeamsViewModel } | null

/**
 * Creates a loader function for the teams view that provides the current
 * workspace's view model, and redirects users who may not see teams.
 *
 * @param iam
 */
export function createTeamsViewLoader(iam: IamFeature): LoaderFunction {
  return async ({ request }): Promise<TeamsViewLoaderData> => {
    const { workspace } = await iam.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const internal = await iam.internal()
    const vm = internal.acquireTeamsViewModel(iam.getCurrentMember())
    if (!vm.canViewTeams) {
      throw redirect(links.welcome())
    }

    return { vm }
  }
}

/**
 * Data returned by {@link createTeamViewLoader}. `null` when no workspace is
 * selected yet, or the teams failed to load, which the teams view shows.
 */
export type TeamViewLoaderData = { vm: TeamViewModel } | null

/**
 * Creates a loader function for a team's view that provides the view model of
 * the team the route names, redirecting to the teams view when the user is
 * not on it.
 *
 * @param iam
 */
export function createTeamViewLoader(iam: IamFeature): LoaderFunction {
  return async ({ params, request }): Promise<TeamViewLoaderData> => {
    const { workspace } = await iam.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const internal = await iam.internal()
    const member = iam.getCurrentMember()
    const teamsVm = internal.acquireTeamsViewModel(member)
    if (!teamsVm.canViewTeams) {
      throw redirect(links.welcome())
    }

    try {
      await teamsVm.fetchForVisit(request.signal)
    } catch (err) {
      if (isAbortError(err)) {
        throw err
      }
      return null
    }

    const team = teamsVm.findTeam(params.teamSlug ?? '')
    if (!team) {
      throw replace(links.team())
    }

    const vm = await internal.acquireTeamViewModel(member, team, request.signal)
    return { vm }
  }
}

/**
 * Data returned by {@link createProfileViewLoader}. `null` when no workspace
 * is selected yet, so the authentication screen is showing.
 */
export type ProfileViewLoaderData = { vm: ProfileViewModel } | null

/**
 * Creates a loader function for the profile view that provides the current
 * workspace's view model.
 *
 * @param iam
 */
export function createProfileViewLoader(iam: IamFeature): LoaderFunction {
  return async ({ request }): Promise<ProfileViewLoaderData> => {
    const { workspace } = await iam.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const internal = await iam.internal()
    const vm = await internal.acquireProfileViewModel(
      iam.getCurrentMember(),
      request.signal,
    )
    return { vm }
  }
}

/**
 * Creates a loader function for a profile page that redirects users who may
 * not see it.
 *
 * @param iam
 * @param canView Whether the user may see the page.
 * @param fallbackUrl Where to send users who may not see the page.
 */
export function createProfilePageLoader(
  iam: IamFeature,
  canView: (vm: ProfileViewModel) => boolean,
  fallbackUrl: string,
): LoaderFunction {
  return async ({ request }) => {
    const { workspace } = await iam.waitForAuthResolution(request.signal)
    if (!workspace) {
      return null
    }

    const internal = await iam.internal()
    const vm = await internal.acquireProfileViewModel(
      iam.getCurrentMember(),
      request.signal,
    )
    if (!canView(vm)) {
      throw redirect(fallbackUrl)
    }

    return null
  }
}
